/**
 * ProductCard — one product on the marketplace slip (STORY-003-005, rebuilt
 * 2026-08 onto the counter-slip label grid).
 *
 * WHAT WAS WRONG. The old card was a tile with a stack of independently-sized
 * things under the photo: a name, a price set in body type at body weight, a
 * green "В наличии" pill, the seller, a SECOND green "✓ Проверенный продавец"
 * pill, then the address. Two green pills under every price meant the two least
 * discriminating facts on the card — everything is in stock, every seller is
 * verified — were also its loudest. The price, the single number the buyer came
 * for, had no more presence than the address below it. And because every block
 * was free-flowing, no two cards agreed on where anything sat: twelve cards
 * read as twelve compositions.
 *
 * WHAT IT IS NOW. A shipping label. Three DRAWN rules divide the card into the
 * same three rows on every single card:
 *
 *   ┌──────────────────┐
 *   │  photo, 4:3      │   honest bazaar photography, one aspect ratio, one
 *   ╞══════════════════╡   token-radius frame, no gradient scrim over it
 *   │  NAME            │   row 1 — two-line clamp at a FIXED height, which is
 *   │                  │           what makes rows 2 and 3 land on the same
 *   ├──────────────────┤           y-offset across the whole grid
 *   │  Dinqi   ● В нал.│   row 2 — the measure: who, where, and availability
 *   │  УСТА · Б37-39   │
 *   ├──────────────────┤
 *   │  29 000 сом    ✓ │   row 3 — the price, as an instrument reading
 *   └──────────────────┘
 *
 * The price is now `.g-data` at 20px/700 in `--g-figure` with the unit beside
 * it at meta weight (see LabelGrid.tsx). Availability is a dot plus a
 * contrast-safe label; verification is a hairline check plus its phrase in
 * `--g-ink-2` — said once, never twice, never by colour alone.
 *
 * LINK SCOPE. The story's AC keeps price and stock visible-but-not-focusable,
 * so exactly ONE anchor exists per card, around the name. It is stretched over
 * the whole card with an `after:` overlay so the entire tile is tappable on a
 * phone (the user is standing in a market, one-handed) without introducing a
 * second tab stop or a nested interactive element. The focus ring therefore
 * lives on the `<article>` via `focus-within:`.
 *
 * The old `group-hover:underline` on the title is GONE — it was rendering as a
 * link-style leak in the grid (one card permanently underlined in the shipped
 * screenshot). Hover now moves the card's rule to `--g-rule-strong` and eases
 * the photo, which is a card-level affordance rather than a text decoration.
 *
 * A0: the card links to `/marketplace/products/:id` (the seller-detail route
 * `/marketplace/sellers/:id` is not mounted; linking there was a reachable 404).
 *
 * Tokens via the `--g-*` mirror; strings from the `marketplace` namespace. No
 * PII renders here — seller phone numbers are not in the browse payload.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import { useGardenMotion } from "@/lib/use-garden-motion";
import {
  PriceFigure,
  sellerBrandName,
  StallLine,
  StockMark,
  VerifiedMark,
} from "@/features/marketplace/LabelGrid";
import type { ProductSearchItem } from "@/features/marketplace/useMarketplaceBrowse";

export interface ProductCardProps {
  product: ProductSearchItem;
  /** 0-based position in the grid (analytics payload). */
  position: number;
  /** Fired on card click (before navigation) — analytics hook. */
  onCardClick?: (product: ProductSearchItem, position: number) => void;
}

/**
 * The product-detail route a card links to.
 * A0: was `/marketplace/sellers/:id` (003-006 seller-detail, never mounted →
 * reachable 404). Repointed to the existing `/marketplace/products/:id` detail
 * page so the browse grid no longer dead-ends.
 */
export function productDetailPath(productId: string): string {
  return `/marketplace/products/${productId}`;
}

export function ProductCard({
  product,
  position,
  onCardClick,
}: ProductCardProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const { press } = useGardenMotion();

  return (
    // Surface-tier press feedback (whileTap scale 0.98), gated on reduced
    // motion via useGardenMotion().press. `relative` anchors the stretched link
    // overlay; `focus-within` carries the ring because the anchor itself is a
    // zero-area pseudo-element spread over the card.
    <motion.article
      data-testid={`product-card-${product.id}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-[var(--g-radius)] border border-[color:var(--g-hairline)] bg-card text-card-foreground transition-colors duration-150 hover:border-[color:var(--g-rule-strong)] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background"
      {...press("surface")}
    >
      {/* The photograph. Real, unglamorous stall photography deserves a fixed
          frame and nothing on top of it — no scrim, no overlay chip. 4:3 rather
          than 1:1 because pallet trucks, ladders and generators are wide
          objects and a square crop guillotines them. */}
      <div className="aspect-[4/3] w-full overflow-hidden bg-[color:var(--g-sunken)]">
        {/* Decorative within the card: the product name renders as visible text
            in row 1 and the link's aria-label already names the product, so an
            alt here would be redundant (axe image-redundant-alt). Empty alt =
            presentational. The hover ease is transform-only (CSP-safe) and is
            suppressed under prefers-reduced-motion. */}
        <img
          src={product.photo_thumb_url}
          alt=""
          loading="lazy"
          width={400}
          height={300}
          // contain, not cover — SCRUM-230 applied to the grid card it missed.
          // The frame keeps its 4:3 rhythm; the photograph stays whole.
          className="size-full object-contain transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none"
        />
      </div>

      {/* The label. `g-ruled` draws the row rules; the top rule is the stronger
          one because it is where the label starts. */}
      <div className="g-ruled flex flex-1 flex-col border-t border-[color:var(--g-rule-strong)]">
        {/* ── Row 1 · NAME ────────────────────────────────────────────────
            Fixed min-height for exactly two clamped lines. This is the whole
            trick behind the label grid: a one-line name and a two-line name
            must not shift the price rule, or the wall stops reading as one
            system. */}
        <div className="flex min-h-[3.25rem] items-start px-3 py-2.5">
          <Link
            to={productDetailPath(product.id)}
            onClick={() => onCardClick?.(product, position)}
            data-testid={`product-card-link-${product.id}`}
            aria-label={t("aria.productLink", {
              seller: product.seller_business_name,
              product: product.name,
            })}
            // `after:` stretches the hit area over the entire card without a
            // second tab stop. `outline-none` is safe: the ring is on the
            // article via focus-within.
            className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
          >
            <h3 className="line-clamp-2 text-[0.9375rem] font-semibold leading-snug text-foreground transition-colors group-hover:text-[color:var(--g-primary)]">
              {product.name}
            </h3>
          </Link>
        </div>

        {/* ── Row 2 · MEASURE ─────────────────────────────────────────────
            Who has it, whether they have it, and which stall to walk to. Per
            the product principles the place is half the payload of any answer,
            so it is a rule of its own rather than a footnote. */}
        <div className="flex flex-col gap-1 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-xs font-medium text-[color:var(--g-ink-2)]">
              {sellerBrandName(
                product.seller_business_name,
                product.pavilion_number,
              )}
            </span>
            <StockMark
              state={product.stock_state}
              testId={`stock-pill-${product.id}`}
            />
          </div>
          <StallLine
            market={product.market_name_ru}
            pavilion={product.pavilion_number}
          />
        </div>

        {/* ── Row 3 · PRICE ───────────────────────────────────────────────
            The hero, and the last rule so it sits on the card's baseline. The
            verified mark rides along at the quietest weight the card has. */}
        <div className="mt-auto flex items-end justify-between gap-2 px-3 py-2.5">
          <PriceFigure
            price={product.price_kgs}
            priceMax={product.price_max_kgs}
            priceKind={product.price_kind}
          />
          <VerifiedMark testId={`product-verified-${product.id}`} />
        </div>
      </div>
    </motion.article>
  );
}
